import { useEffect } from "react";

/**
 * Iter128 — L0.5 SEO sanity · per-route head management.
 * Iter130 — Made hostname-aware for the qortrace.com / qorbom.com
 *           dual-deployment architecture. Callers now pass `canonicalPath`
 *           (relative — e.g. `/trust`) and the hook resolves it against
 *           the runtime hostname so a visitor on `qorbom.com` gets a
 *           `<link rel=canonical>` pointing at `qorbom.com`, not the
 *           QorTrace flagship.
 *
 * SPA gotcha: every public URL serves the same static index.html with the
 * same title + description + canonical. Google's renderer DOES execute
 * JavaScript and re-reads <title>, but a sizable share of crawlers
 * (LinkedIn unfurl, Bing bot, Bytespider, embed previews, sitemap
 * indexing) only read the static HTML.
 *
 * `usePageMeta()` fixes the runtime side: at every route mount it sets
 *   - document.title
 *   - <meta name="description"> content
 *   - <link rel="canonical"> href (creates the link if absent)
 *
 * For the truly-crawler-blind fallback we still need server-side meta
 * injection, but that's a separate piece of work; this hook covers
 * Google + JS-aware crawlers, which is the majority of search traffic.
 *
 * Usage — preferred (hostname-aware):
 *   usePageMeta({
 *     title:         "Trust Posture — QorTrace",
 *     description:   "QorTrace's live 9-layer security posture, refreshed every minute.",
 *     canonicalPath: "/trust",
 *   });
 *
 * Legacy (hardcoded URL still accepted for backward compat):
 *   usePageMeta({ title, description, canonical: "https://qortrace.com/trust" });
 *
 * Convention:
 *   - Titles: short, end with " — QorTrace", < 60 chars total.
 *   - Descriptions: 1 sentence, < 160 chars, hooks the click.
 *   - canonicalPath: leading slash, no query string, no trailing slash
 *     (except homepage which is "/").
 */
export default function usePageMeta({ title, description, canonical, canonicalPath } = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previousTitle = document.title;
    const previousDesc  = getMeta("description");
    const previousCanon = getCanonical();

    if (title) document.title = title;
    if (description) setMeta("description", description);
    const resolvedCanonical = resolveCanonical({ canonical, canonicalPath });
    if (resolvedCanonical) setCanonical(resolvedCanonical);

    return () => {
      // Restore prior values on unmount so the next route's hook doesn't
      // see a stale value before its own effect fires.
      if (title && document.title === title && previousTitle) {
        document.title = previousTitle;
      }
      if (description && previousDesc != null) {
        setMeta("description", previousDesc);
      }
      if (resolvedCanonical && previousCanon != null) {
        setCanonical(previousCanon);
      }
    };
  }, [title, description, canonical, canonicalPath]);
}


// --- brand-aware base URL resolution -------------------------------------

/**
 * Resolve the canonical href for the current page.
 * Priority:
 *   1. Caller passed an explicit absolute `canonical` URL → use as-is.
 *   2. Caller passed `canonicalPath` (relative) → derive from hostname.
 *
 * Hostname mapping (production):
 *   qortrace.com / www.qortrace.com       → https://qortrace.com
 *   qorbom.com   / www.qorbom.com         → https://qorbom.com
 *   *.preview.emergentagent.com (preview) → https://qortrace.com (so
 *     preview pages still canonical-redirect to production, not
 *     leak the preview URL into Google's index)
 *   anything else (localhost, custom)     → window.location.origin
 */
export function getCanonicalBase() {
  if (typeof window === "undefined") return "https://qortrace.com";
  const host = (window.location.hostname || "").toLowerCase();
  if (host === "qorbom.com" || host === "www.qorbom.com") {
    return "https://qorbom.com";
  }
  if (host === "qortrace.com" || host === "www.qortrace.com") {
    return "https://qortrace.com";
  }
  // Preview pods + any other staging host — pin to qortrace.com to keep
  // the preview URL out of search indexes.
  if (host.endsWith(".preview.emergentagent.com") || host.endsWith(".emergent.host")) {
    return "https://qortrace.com";
  }
  // Localhost / unknown — use the actual origin so dev links work.
  return window.location.origin;
}

function resolveCanonical({ canonical, canonicalPath }) {
  if (canonical) return canonical;
  if (!canonicalPath) return null;
  const base = getCanonicalBase();
  const path = canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`;
  return `${base}${path}`;
}


// --- small DOM helpers ----------------------------------------------------

function getMeta(name) {
  const el = document.querySelector(`meta[name="${name}"]`);
  return el ? el.getAttribute("content") : null;
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function getCanonical() {
  const el = document.querySelector('link[rel="canonical"]');
  return el ? el.getAttribute("href") : null;
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
